import { Bone, Euler, Group, Quaternion, Vector3 } from "three";
import { paused, playerState, setPlayerState } from "@stores";
class GunController {
    group;
    selected;
    positionIdle;
    positionAiming;
    rotation;
    timeToADSMultiplier;
    loadout;
    /**
     * Controller for an entitie's weapons.
     * @param primary The entity's primary gun.
     * @param secondary The entity's secondary gun.
     */
    constructor(primary, secondary) {
        this.selected = "primaryWeapon";
        this.timeToADSMultiplier = 10;
        this.positionIdle = new Vector3(0.001, 0.001, -0.007);
        this.positionAiming = new Vector3(0, 0.00207, -0.007);
        this.rotation = new Quaternion().setFromEuler(new Euler(0, -Math.PI, 0));
        this.group = this.createGroup();
        this.loadout = {
            primaryWeapon: primary,
            secondaryWeapon: secondary,
        };
        this.group.add(this.getSelected.getMeshController.getMesh);
    }
    /**
     * Perform a step on the currently held weapon.
     * @param entity The entity that holds the weapon.
     * @param entities The entities that can be aimed at.
     * @param ground the game's ground.
     * @param inputController The entity's input controller.
     * @param deltaTime The interval in seconds from the last frame to the current one.
     */
    step(entity, entities, ground, inputController, deltaTime) {
        if (paused()) {
            this.synchronizeHands(entity);
            return;
        }
        if (inputController.getAIMING && !this.group.position.equals(this.positionAiming)) {
            this.group.position.lerp(this.positionAiming, deltaTime * this.timeToADSMultiplier);
        }
        else if (!inputController.getAIMING && !this.group.position.equals(this.positionIdle)) {
            this.group.position.lerp(this.positionIdle, deltaTime * this.timeToADSMultiplier);
        }
        this.rotateWeapon(entity, entities, ground);
        this.getSelected.step(entity, inputController);
        this.synchronizeHands(entity);
    }
    /**
     * Change to a different type of weapon.
     * For example, switch between `primary` and `secondary` weapons.
     * @param type The weapon type to switch to.
     */
    changeWeapon(type) {
        this.group.remove(this.getSelected.getMeshController.getMesh);
        this.selected = type;
        const selectedGun = this.getSelected;
        setPlayerState({
            ...playerState(),
            gun: {
                name: selectedGun.getName,
                imageSource: selectedGun.getImageSource,
                firingMode: selectedGun.getFiringModeManager.getFiringMode.getShortName,
                allowedFiringModes: selectedGun.getFiringModeManager.getAllowedFiringModes.map(allowedFiringMode => allowedFiringMode.getShortName),
            },
        });
        this.group.add(this.getSelected.getMeshController.getMesh);
    }
    /**
     * Rotate the group so that it aims at whatever is in fron of the entity.
     * If there is nothing, do nothing.
     * @param entity The entity that holds the weapon.
     * @param entities The entities that the entity could be looking at.
     * @param ground The terrain.
     */
    rotateWeapon(entity, entities, ground) {
        const intersections = entity.getRaycaster.intersectObjects([
            ...entities.map(_entity => _entity.getGroup),
            ground.getGroup,
        ]);
        if (intersections.length === 0)
            return;
        const target = intersections[0].point;
        this.group.lookAt(target);
    }
    /**
     * Synchronize the entity's hands with the gun that it holds.
     * @param entity The entity to synchronize the hands of.
     * @throws If a hand bone has no parent.
     */
    synchronizeHands(entity) {
        this.synchronizeHand(entity.getSkeleton.handLeft.getBone, this.getSelected.getSkeleton.foregrip.getBone);
        this.synchronizeHand(entity.getSkeleton.handRight.getBone, this.getSelected.getSkeleton.handle.getBone);
    }
    /**
     * Synchronize the position of an origin bone with a target bone.
     * Make sure that the origin bone has a parent bone.
     * @param origin The origin bone.
     * @param target The target bone.
     * @throws If the origin bone has no parent.
     */
    synchronizeHand(origin, target) {
        const foregripWorldPosition = new Vector3();
        target.getWorldPosition(foregripWorldPosition);
        if (!origin.parent || !(origin.parent instanceof Bone))
            throw new Error("The hand cannot be moved to its position on the gun, it has no parent bone.");
        const localOffset = origin.parent.worldToLocal(foregripWorldPosition);
        origin.position.copy(localOffset);
    }
    /**
     * Create a group object.
     * @returns the create group object.
     * @private
     */
    createGroup() {
        const group = new Group();
        group.position.copy(this.positionIdle);
        group.quaternion.copy(this.rotation);
        return group;
    }
    get getGroup() {
        return this.group;
    }
    get getSelected() {
        return this.loadout[this.selected];
    }
}
export { GunController };
